import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function SceneHero() {
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = root.current;
    if (!el) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: el,
          start: "top top",
          end: "+=180%",
          scrub: 0.8,
          pin: true,
          anticipatePin: 1,
        },
      });

      // metadata reveal in
      tl.from(".hero-meta", {
        yPercent: 100,
        opacity: 0,
        stagger: 0.05,
        duration: 0.4,
      }, 0);

      // EMI letters shatter off
      tl.to(".hero-letter-E", { yPercent: 120, opacity: 0, duration: 0.6, ease: "power3.in" }, 0.15);
      tl.to(".hero-letter-M", { yPercent: 140, opacity: 0, duration: 0.6, ease: "power3.in" }, 0.22);
      tl.to(".hero-letter-I", { yPercent: 160, opacity: 0, duration: 0.6, ease: "power3.in" }, 0.28);

      // R scales up massively
      tl.to(".hero-letter-R", {
        scale: 10,
        duration: 1,
        ease: "power2.inOut",
        transformOrigin: "50% 60%",
      }, 0.7);

      // inner mask fills reveal, letter shifts to almost fill viewport
      tl.to(".hero-mask-inner", {
        opacity: 1,
        duration: 0.3,
      }, 1.0);

      // outgoing: fade top area
      tl.to(".hero-descriptor, .hero-meta, .hero-cta", {
        opacity: 0,
        duration: 0.3,
      }, 0.6);

      // final push: shift up, revealing the diagonal leg to next scene
      tl.to(".hero-letter-R", {
        yPercent: -30,
        duration: 0.4,
        ease: "power2.in",
      }, 1.5);
    }, root);

    return () => ctx.revert();
  }, []);

  return (
    <section
      id="scene-hero"
      ref={root}
      data-theme="dark"
      className="relative h-screen w-full overflow-hidden ink-bg grain"
    >
      {/* metadata top */}
      <div className="absolute top-24 left-0 right-0 px-6 md:px-10 flex items-start justify-between type-label text-[color:var(--gray)]">
        <div className="hero-meta">
          <div>N 40.7128</div>
          <div>W 74.0060</div>
        </div>
        <div className="hero-meta text-center">
          <div>REEL 001 / TAKE 07</div>
          <div className="mt-1">SOLO OPERATOR</div>
        </div>
        <div className="hero-meta text-right">
          <div>2026 · Q3</div>
          <div>AVAILABLE FOR HIRE</div>
        </div>
      </div>

      {/* Enormous type */}
      <div
        className="absolute inset-0 flex items-center justify-center select-none"
        aria-hidden="true"
      >
        <h1
          className="type-display flex text-[color:var(--paper)]"
          style={{ fontSize: "40vw", lineHeight: 0.8 }}
        >
          <span className="hero-letter-R inline-block relative">
            R
            <span
              className="hero-mask-inner absolute inset-0 flex items-center justify-center type-mono text-[color:var(--signal)] opacity-0"
              style={{ fontSize: "1.2vw" }}
            >
              <span className="opacity-70">ENTER →</span>
            </span>
          </span>
          <span className="hero-letter-E inline-block">E</span>
          <span className="hero-letter-M inline-block">M</span>
          <span className="hero-letter-I inline-block">I</span>
        </h1>
      </div>

      {/* real h1 for SEO */}
      <h1 className="sr-only">REMI — Solo AI Builder. Independent full-stack systems shipping now.</h1>

      {/* descriptor */}
      <div className="absolute bottom-40 left-6 md:left-10 hero-descriptor">
        <div className="type-mono text-xs text-[color:var(--gray)]">
          <div>[ AI BUILDER ]</div>
          <div className="mt-2 text-[color:var(--paper)]">SOLO / INDEPENDENT / FULL-STACK</div>
        </div>
      </div>

      <div className="absolute bottom-40 right-6 md:right-10 hero-cta text-right">
        <div className="type-mono text-xs text-[color:var(--gray)]">SCROLL TO ENTER</div>
        <div className="type-mono text-xs text-[color:var(--paper)] mt-2">↓ 000 → 100</div>
      </div>

      {/* bottom line */}
      <div className="absolute bottom-6 left-0 right-0 px-6 md:px-10 flex items-end justify-between">
        <div className="type-label text-[color:var(--gray)]">SIGNAL · 01</div>
        <div className="type-label text-[color:var(--gray)]">REMI.AI</div>
        <div className="type-label text-[color:var(--gray)]">
          <span className="inline-block w-2 h-2 bg-[color:var(--signal)] mr-2 align-middle" />
          ONLINE
        </div>
      </div>
    </section>
  );
}